import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import React from 'react';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetOpenWooRequestDetail, getServerPageGetWooRequestSlugs } from 'api/graphql-helper';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import {
	PageInformationFieldsFragment,
	WooDocumentDetailSingleFieldsFragment,
	WooRequestFieldsFragment
} from 'api/graphql-types';

import { IBasePageProps } from 'types/page';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { OPEN_GELDERLAND_ROUTES, ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

import Layout from 'modules/layout/layout';
import OpenHeader from 'modules/open-header';
import OpenBody from 'modules/open-body/open-body';
import OpenDocuments from 'modules/open-documents';
import { ModuleBuilder } from 'modules/module-builder/module-builder';

interface IWooDocumentDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	wooRequestBySlug?: WooRequestFieldsFragment;
	wooDocumentDetail?: WooDocumentDetailSingleFieldsFragment;
	id?: string;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const WooDocumentDetailPage = (props: IWooDocumentDetailPageProps): React.JSX.Element => {
	const router = useRouter();
	const page = props.wooRequestBySlug;
	const seoSingle = props?.pageInformation?.seo;
	const modules = props.wooDocumentDetail?.modules;

	if (!page) return <></>;

	const onSearch = async (search: string): Promise<void> => {
		router.push({ pathname: '/woo-documenten', query: { zoekterm: search } });
	};

	return (
		<Layout
			contentId={props?.id}
			typeName={page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
			customSearch={onSearch}
		>
			<OpenHeader {...page} seo={seoSingle} />
			<OpenBody {...page} />
			<OpenDocuments {...page} />
			<ModuleBuilder modules={modules} slug={OPEN_GELDERLAND_ROUTES.WOO_DOCUMENTS.fullPath} />
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetWooRequestSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.WOO_REQUESTS.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.WOO_REQUESTS.slug}/${ctx.params.slug}` : '/';
		const variables = { slug, locale, publicationState };

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::woo-request.woo-request',
			contentTypeName: 'woo-request',
			neutralStyle: false,
			hideLanguageSwitcher: true,
			hideSearchbar: false,
			isSubsite: true,
			fetchPage: () => getServerPageGetOpenWooRequestDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.openGelderlandInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.wooRequestBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		let wooDocumentDetail = null;
		if (!passwordResult.isPasswordProtected && passwordResult.page) {
			const detail = await getServerPageGetOpenWooRequestDetail({ variables }, client);
			wooDocumentDetail = detail.props.data?.wooDocumentDetail?.data?.attributes || null;
		}

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				pageInformation: passwordResult.pageInformation,
				wooRequestBySlug: passwordResult.page,
				wooDocumentDetail: wooDocumentDetail,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in WOO request detail', error);
		return { notFound: true };
	}
};

export default WooDocumentDetailPage;
